import React, { useState, useEffect, useCallback, memo } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, PauseCircle, PlayCircle, RefreshCw, Database, Download, Upload, Settings } from "lucide-react";
import { toast } from "sonner";
import { 
  setSystemStatus,
  performFullAggregation,
  performFullAlgoliaSync
} from '@/lib/api';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectAllDrinks,
  selectGlobalDataLoading,
  selectGlobalDataInitialized
} from '@/store/slices/globalDataSlice';
import { 
  selectUser
} from '@/store/slices/authSlice';
import { 
  selectIsAggregationEnabled, 
  selectIsAlgoliaEnabled,
  initializeAdminListeners,
  cleanupAdminListeners,
  selectSystemStatusLoading
} from '@/store/slices/adminSlice';
import { selectMapBounds } from '@/store/slices/mapUiSlice';
import { importFirestoreData } from '@/utils/initFirestore';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AlgoliaUsageStats } from '@/components/management/AlgoliaUsageStats';
import { Checkbox } from "@/components/ui/checkbox"
import { fetchLocations } from '@/store/slices/locationsSlice';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import AdminStatusDisplay from '@/components/ui/AdminStatusDisplay';

// Add interface definitions for missing types
interface Coordinates {
  latitude: number;
  longitude: number;
}

type LocationType = "restaurant" | "shop" | "brewery" | "other";
type ActionMode = 'import' | 'download';

// Define status types based on documentation
type SystemStatusValue = 'enabled' | 'disabled';

interface SystemStatus {
  aggregation: SystemStatusValue;
  algolia: SystemStatusValue;
}

// Main component - only renders the button and conditionally the sheet content
const AlgoliaAdminBox: React.FC = () => {
  // State to track if the side sheet is open
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  // Get system statuses from Redux for the button color
  const aggregationEnabled = useAppSelector(selectIsAggregationEnabled);
  const algoliaEnabled = useAppSelector(selectIsAlgoliaEnabled);
  
  return (
    <>
      {/* Sheet with trigger button */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="absolute bottom-4 right-4 z-10 bg-background/95 backdrop-blur-sm shadow-lg border border-border rounded-full h-12 w-12"
          >
            {/* Icon color indicates if *any* system is disabled */}
            <Settings className={`h-6 w-6 ${
              !aggregationEnabled || !algoliaEnabled ? 'text-destructive' : ''
            }`} />
            <span className="sr-only">Open Admin Panel</span>
          </Button>
        </SheetTrigger>

        {/* Only render the panel content when sheet is open */}
        {isSheetOpen && <AdminPanelContent />}
      </Sheet>
    </>
  );
};

// Memoized panel content component
const AdminPanelContent = memo(() => {
  const dispatch = useAppDispatch();

  // Redux states
  const aggregationEnabled = useAppSelector(selectIsAggregationEnabled);
  const algoliaEnabled = useAppSelector(selectIsAlgoliaEnabled);
  const systemStatusLoading = useAppSelector(selectSystemStatusLoading);
  const currentUser = useAppSelector(selectUser);
  const userId = currentUser?.id || null;
  const mapBounds = useAppSelector(selectMapBounds);
  const drinks = useAppSelector(selectAllDrinks);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);
  
  // Control states
  const [isTogglingAggregation, setIsTogglingAggregation] = useState(false);
  const [isTogglingAlgolia, setIsTogglingAlgolia] = useState(false);
  const [isPerformingAggregation, setIsPerformingAggregation] = useState(false);
  const [isPerformingAlgoliaSync, setIsPerformingAlgoliaSync] = useState(false);
  const [isPerformingRecalculationAndSync, setIsPerformingRecalculationAndSync] = useState(false);
  const [isGeneratingData, setIsGeneratingData] = useState(false);
  const [isExportingFirestore, setIsExportingFirestore] = useState(false);
  
  // Accordion state
  const [openAccordion, setOpenAccordion] = useState<string | undefined>("system-status");
  
  // AlgoliaUsageStats refresh key
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  
  // Test data state
  const [numLocations, setNumLocations] = useState<number>(10);
  const [minSightings, setMinSightings] = useState<number>(2);
  const [maxSightings, setMaxSightings] = useState<number>(5);
  const [minConfirmations, setMinConfirmations] = useState<number>(1);
  const [maxConfirmations, setMaxConfirmations] = useState<number>(5);
  const [confirmationDays, setConfirmationDays] = useState<number>(30);
  const [actionMode, setActionMode] = useState<ActionMode>('import');
  const [runRecalculationAndSync, setRunRecalculationAndSync] = useState<boolean>(false);
  
  // Validation state
  const [validationIssues, setValidationIssues] = useState<Array<{severity: 'error' | 'warning'; message: string}>>([]);
  const [showValidationResults, setShowValidationResults] = useState(false);
  
  // Derived states
  const drinksLoading = globalDataLoading === 'pending' || (globalDataLoading === 'idle' && !globalDataInitialized);
  const isLoading = systemStatusLoading || isTogglingAggregation || isTogglingAlgolia || 
                    isPerformingAggregation || isPerformingAlgoliaSync || 
                    isPerformingRecalculationAndSync || isGeneratingData || 
                    isExportingFirestore;
  const isTestDataGenDisabled = isLoading || drinksLoading || (drinks || []).length === 0;

  // Initialize admin listeners when component mounts
  useEffect(() => {
    dispatch(initializeAdminListeners());
    return () => {
      dispatch(cleanupAdminListeners());
    };
  }, [dispatch]);

  // Handler for accordion change
  const handleAccordionChange = useCallback((value: string | undefined): void => {
    setOpenAccordion(value);
    
    // Refresh stats when Algolia stats section is opened
    if (value === "algolia-stats") {
      setStatsRefreshKey(prevKey => prevKey + 1);
    }
  }, []);

  // Handler to manually refresh stats
  const refreshStats = useCallback((): void => {
    setStatsRefreshKey(prevKey => prevKey + 1);
    toast.info("Refreshing Algolia usage statistics...");
  }, []);

  // Handler for toggling Aggregation status
  const handleToggleAggregation = useCallback(async (): Promise<void> => {
    setIsTogglingAggregation(true);
    const newStatus = aggregationEnabled ? 'disabled' : 'enabled';
    const actionText = newStatus === 'enabled' ? 'Enabling' : 'Disabling';

    try {
      await setSystemStatus({ aggregationStatus: newStatus });
      toast.success(`Aggregation triggers ${actionText.toLowerCase()}d successfully.`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`Error ${actionText.toLowerCase()} aggregation triggers:`, error);
      toast.error(`Failed to ${actionText.toLowerCase()} aggregation triggers: ${errorMessage}`);
    } finally {
      setIsTogglingAggregation(false);
    }
  }, [aggregationEnabled]);
  
  // Handler for toggling Algolia Sync status
  const handleToggleAlgolia = useCallback(async (): Promise<void> => {
    setIsTogglingAlgolia(true);
    const newStatus = algoliaEnabled ? 'disabled' : 'enabled';
    const actionText = newStatus === 'enabled' ? 'Enabling' : 'Disabling';

    try {
      await setSystemStatus({ algoliaStatus: newStatus });
      toast.success(`Algolia sync ${actionText.toLowerCase()}d successfully.`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`Error ${actionText.toLowerCase()} Algolia sync:`, error);
      toast.error(`Failed to ${actionText.toLowerCase()} Algolia sync: ${errorMessage}`);
    } finally {
      setIsTogglingAlgolia(false);
    }
  }, [algoliaEnabled]);

  // Handler for triggering full aggregation recalculation
  const handlePerformAggregation = useCallback(async (): Promise<void> => {
    setIsPerformingAggregation(true);
    toast.info("Starting full data aggregation recalculation...");
    toast.warning("This will disable both Aggregation and Algolia Sync. They must be manually re-enabled after completion.");
    try {
      await performFullAggregation();
      toast.success("Full data aggregation recalculation completed successfully.");
      toast.info("System statuses (Aggregation & Algolia Sync) remain disabled. Please re-enable them manually.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error recalculating aggregations:", error);
      toast.error(`Aggregation recalculation failed: ${errorMessage}`);
    } finally {
      setIsPerformingAggregation(false);
    }
  }, []);
  
  // Handler for triggering full Algolia sync
  const handlePerformAlgoliaSync = useCallback(async (): Promise<void> => {
    setIsPerformingAlgoliaSync(true);
    toast.info("Starting full Algolia index sync...");
    toast.warning("This will temporarily disable Algolia Sync during the operation.");
    try {
      await performFullAlgoliaSync();
      toast.success("Full Algolia index sync completed successfully.");
      toast.info("Algolia Sync status has been restored.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error syncing Algolia:", error);
      toast.error(`Algolia sync failed: ${errorMessage}`);
    } finally {
      setIsPerformingAlgoliaSync(false);
    }
  }, []);
  
  // Handler for triggering both recalculation and sync
  const handlePerformRecalculationAndSync = useCallback(async (): Promise<void> => {
    setIsPerformingRecalculationAndSync(true);
    toast.info("Starting full data recalculation and Algolia sync...");
    toast.warning("This process involves multiple steps and status changes. Monitor toasts for progress.");
    
    let aggregationSucceeded = false;
    try {
      // Step 1: Perform Aggregation (Disables both, doesn't re-enable)
      toast.info("Step 1/2: Performing full data aggregation recalculation...");
      toast.warning("Both Aggregation and Algolia Sync will be disabled.");
      await performFullAggregation();
      toast.success("Step 1/2: Aggregation recalculation completed.");
      toast.info("System statuses remain disabled for the next step.");
      aggregationSucceeded = true;
      
      // Step 2: Perform Algolia Sync (Temporarily disables/re-enables Algolia)
      toast.info("Step 2/2: Performing full Algolia index sync...");
      await performFullAlgoliaSync();
      toast.success("Step 2/2: Full Algolia index sync completed.");
      
      // Final Status Update
      toast.success("Recalculation and Sync process finished.");
      toast.info("Algolia Sync has been re-enabled, but Aggregation remains disabled. Please re-enable Aggregation manually.");
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error during Recalculation & Sync:", error);
      if (aggregationSucceeded) {
        toast.error(`Algolia sync (Step 2/2) failed: ${errorMessage}`);
        toast.info("Aggregation (Step 1/2) completed, but sync failed. Aggregation remains disabled. Please re-enable manually and consider re-running sync.");
      } else {
        toast.error(`Aggregation recalculation (Step 1/2) failed: ${errorMessage}`);
        toast.info("Process stopped. System statuses may be disabled. Check status and re-enable manually if needed.");
      }
    } finally {
      setIsPerformingRecalculationAndSync(false);
    }
  }, []);

  // Generate random coordinates around map center
  const getRandomCoordinates = (): Coordinates => {
    // If mapBounds is not available, use default coordinates
    if (!mapBounds) {
      console.warn("[AlgoliaAdminBox] Map bounds not available for generating coordinates. Using default (0,0).");
      return { latitude: 0, longitude: 0 }; 
    }
    
    // Calculate center from bounds
    const mapCenterLat = (mapBounds.north + mapBounds.south) / 2;
    const mapCenterLng = (mapBounds.east + mapBounds.west) / 2;

    // Calculate the span of the current map view
    const latSpan = Math.abs(mapBounds.north - mapBounds.south);
    const lngSpan = Math.abs(mapBounds.east - mapBounds.west);
    // Handle longitude wrap-around (less common for typical views)
    // const lngSpan = mapBounds.east >= mapBounds.west 
    //   ? mapBounds.east - mapBounds.west 
    //   : (180 - mapBounds.west) + (mapBounds.east + 180);

    // Define a factor to control spread (e.g., 0.8 = 80% of the view span)
    const spreadFactor = 0.8;
    
    // Generate random offsets based on the view span
    // (Math.random() - 0.5) gives a range from -0.5 to +0.5
    const latOffset = (Math.random() - 0.5) * latSpan * spreadFactor;
    const lngOffset = (Math.random() - 0.5) * lngSpan * spreadFactor;
    
    return {
      latitude: mapCenterLat + latOffset,
      longitude: mapCenterLng + lngOffset
    };
  };

  // Generate random location name
  const getRandomLocationName = (): string => {
    const prefixes = [
      "Sunny", "Misty", "Golden", "Blue", "Red", "Green", 
      "Silver", "Rustic", "Urban", "Vintage", "Modern", "Cozy"
    ];
    
    const types = [
      "Cafe", "Diner", "Bistro", "Grocery", "Market", "Shop",
      "Store", "Restaurant", "Pub", "Bar", "Brewery", "Corner Store"
    ];
    
    const suffixes = [
      "Corner", "Plaza", "Street", "Avenue", "Square", "Place",
      "Junction", "Hub", "Stop", "Center", "Spot", "Zone"
    ];
    
    const usePrefix = Math.random() > 0.3;
    const useSuffix = Math.random() > 0.6;
    
    const prefix = usePrefix ? prefixes[Math.floor(Math.random() * prefixes.length)] + " " : "";
    const type = types[Math.floor(Math.random() * types.length)];
    const suffix = useSuffix ? " " + suffixes[Math.floor(Math.random() * suffixes.length)] : "";
    
    return `${prefix}${type}${suffix}`;
  };

  // Generate random location type
  const getRandomLocationType = (): LocationType => {
    const types: LocationType[] = ["restaurant", "shop", "brewery", "other"];
    return types[Math.floor(Math.random() * types.length)];
  };

  // Get random date between now and X days ago
  const getRandomDateInPastXDays = (days: number): Timestamp => {
    const now = new Date();
    const daysAgo = new Date();
    daysAgo.setDate(now.getDate() - days);
    
    const randomTime = daysAgo.getTime() + Math.random() * (now.getTime() - daysAgo.getTime());
    return Timestamp.fromDate(new Date(randomTime));
  };

  // Get random date between now and 30 days ago
  const getRandomDateInPast30Days = (): Timestamp => {
    return getRandomDateInPastXDays(30);
  };

  // Generate random username
  const getRandomUsername = (): string => {
    const prefixes = ['cool', 'super', 'happy', 'soda', 'fizzy', 'bubbly', 'sweet', 'sparkling'];
    const suffixes = ['drinker', 'fan', 'lover', 'hunter', 'tracker', 'finder', 'explorer'];
    const randomNumber = Math.floor(Math.random() * 1000);
    
    return `${prefixes[Math.floor(Math.random() * prefixes.length)]}${suffixes[Math.floor(Math.random() * suffixes.length)]}${randomNumber}`;
  };
  
  // Generate a dummy user ID
  const getDummyUserId = (): string => {
    return uuidv4().substring(0, 20); // Shortened UUID for dummy user ID
  };

  // Get random rating with two decimals between 1 and 5
  const getRandomRating = (): number => {
    return Math.round((1 + Math.random() * 4) * 100) / 100;
  };

  // Download the JSON data as a file
  const downloadJsonFile = (jsonData: string, filename: string = 'test-data.json') => {
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Generate test data
  const generateTestData = async (): Promise<void> => {
    setIsGeneratingData(true);
    setValidationIssues([]);
    setShowValidationResults(false);
    let originalStatuses: SystemStatus | null = null;
    
    try {
      if (drinksLoading || (drinks || []).length === 0) {
        toast.error("Drinks data still loading or unavailable.");
        return;
      }
      
      if (!userId) {
        toast.error("You must be logged in to generate test data with visited locations");
        return;
      }
      
      // Data structure for import with collections and subcollections properly specified
      // Following the model in docs/1-firestore-data-model.md
      const importData: any = {
        locations: [],
        sightings: []
      };
      
      // Create a separate array for the visitedLocations subcollection
      // The path format tells the import system this is a subcollection
      importData[`users/${userId}/visitedLocations`] = {};
      
      // Generate locations
      for (let i = 0; i < numLocations; i++) {
        const locationId = uuidv4();
        const locationName = getRandomLocationName();
        const coordinates = getRandomCoordinates();
        const locationType = getRandomLocationType();
        
        // Create location
        importData.locations.push({
          id: locationId,
          name: locationName,
          address: `${Math.floor(Math.random() * 999) + 1} Test Street`,
          url: "https://example.com",
          locationType,
          coordinates: {
            _type: 'geopoint',
            latitude: coordinates.latitude,
            longitude: coordinates.longitude
          },
          // Add timestamp fields
          createdAt: {
            _type: 'timestamp',
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0
          },
          updatedAt: {
            _type: 'timestamp',
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0
          },
          // Initialize computed fields
          averageRating: 0,
          ratingCount: 0,
          drinkCount: 0,
          lastSightingDate: {
            _type: 'timestamp',
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0
          }
        });
        
        // Generate random number of sightings for this location
        const sightingsCount = Math.floor(Math.random() * (maxSightings - minSightings + 1)) + minSightings;
        
        for (let j = 0; j < sightingsCount; j++) {
          // Pick a random drink
          const drinksArray = drinks || [];
          if (drinksArray.length === 0) {
            continue; // Skip if no drinks available
          }
          
          const randomDrink = drinksArray[Math.floor(Math.random() * drinksArray.length)];
          const sightingDate = getRandomDateInPastXDays(confirmationDays);
          const sightingId = uuidv4();
          
          // Generate random number of confirmations for this sighting
          const confirmationsCount = Math.floor(Math.random() * (maxConfirmations - minConfirmations + 1)) + minConfirmations;
          
          // Create confirmations subcollection path for this sighting
          importData[`sightings/${sightingId}/confirmations`] = [];
          
          // Update confirmationCount based on actual number of generated confirmations
          const confirmationCount = confirmationsCount;
          
          // Create sighting (following the Sighting interface in the data model)
          importData.sightings.push({
            id: sightingId,
            locationId,
            locationName,
            drinkId: randomDrink.id,
            drinkName: randomDrink.name,
            brandId: randomDrink.brandId,
            firstSighted: {
              _type: 'timestamp',
              seconds: sightingDate.seconds,
              nanoseconds: sightingDate.nanoseconds
            },
            lastConfirmed: {
              _type: 'timestamp',
              seconds: sightingDate.seconds,
              nanoseconds: sightingDate.nanoseconds
            },
            confirmationCount
          });
          
          // Generate confirmations for this sighting
          for (let k = 0; k < confirmationsCount; k++) {
            // First confirmation is from the original sighting date
            const confirmationDate = k === 0 
              ? sightingDate 
              : getRandomDateInPastXDays(confirmationDays);
            
            // Create a dummy user for this confirmation
            const dummyUserId = getDummyUserId();
            const dummyUsername = getRandomUsername();
            
            // Add confirmation to the subcollection (following the Confirmation interface)
            importData[`sightings/${sightingId}/confirmations`].push({
              id: uuidv4(), // Each confirmation gets its own ID
              timestamp: {
                _type: 'timestamp',
                seconds: confirmationDate.seconds,
                nanoseconds: confirmationDate.nanoseconds
              },
              userId: dummyUserId,
              username: dummyUsername,
              createdAt: {
                _type: 'timestamp',
                seconds: confirmationDate.seconds,
                nanoseconds: confirmationDate.nanoseconds
              }
            });
          }
        }
        
        // 75% chance to mark location as visited
        if (Math.random() < 0.75) {
          const rating = getRandomRating();
          const visitDate = getRandomDateInPast30Days();
          
          // Ensure the subcollection path exists in importData
          if (!importData[`users/${userId}/visitedLocations`]) {
            importData[`users/${userId}/visitedLocations`] = {}; // Use an object to set docs by ID
          }

          // Add to the visitedLocations subcollection for this user
          // using locationId as the document ID.
          // The importFirestoreData utility should be able to handle
          // an object for a collection path where keys are document IDs.
          importData[`users/${userId}/visitedLocations`][locationId] = {
            // id: locationId, // The key itself is the ID, so this line might be redundant depending on importer
            locationId: locationId, // Explicitly include locationId in the document data
            rating,
            publicNote: "",
            privateNote: "",
            visitDate: {
              _type: 'timestamp',
              seconds: visitDate.seconds,
              nanoseconds: visitDate.nanoseconds
            },
            locationName
          };
        }
      }
      
      // Convert to JSON
      const jsonData = JSON.stringify(importData, null, 2);
      
      if (actionMode === 'download') {
        // Download the JSON file
        downloadJsonFile(jsonData, `soda-tracker-test-data-${numLocations}-locations.json`);
        toast.success(`Successfully generated and downloaded test data with ${numLocations} locations`);
      } else {
        // Validate the data first
        try {
          const { validateImportData } = await import('@/utils/firestore/validation');
          const validationResult = validateImportData(jsonData);
          
          // Track validation issues
          setValidationIssues(validationResult.issues);
          
          // If there are issues, show validation summary dialog
          if (validationResult.issues.length > 0) {
            const errorCount = validationResult.issues.filter(i => i.severity === 'error').length;
            const warningCount = validationResult.issues.filter(i => i.severity === 'warning').length;
            
            console.log(`Validation results: ${errorCount} errors, ${warningCount} warnings`);
            
            // Show validation summary
            setShowValidationResults(true);
            // Don't set loading false yet, user might continue
            
            // If validation failed with errors, stop the import process
            if (!validationResult.valid) {
              setIsGeneratingData(false); // Stop here if errors
              return; 
            }
            
            // If only warnings, allow user to continue from the validation dialog
            if (warningCount > 0 && errorCount === 0) {
              toast.warning(`Generated data has ${warningCount} warnings. Review in validation results before continuing.`);
              // Keep loading true, dialog is open
              return; 
            }
          }
        } catch (validationError) {
          const errorMessage = validationError instanceof Error ? validationError.message : String(validationError);
          console.error("Error validating test data:", validationError);
          toast.error(`Failed to validate test data: ${errorMessage}`);
          setIsGeneratingData(false);
          return;
        }
        
        // *** Disable system triggers before import ***
        try {
          toast.info("Disabling Aggregation and Algolia triggers for import...");
          originalStatuses = { // Store original statuses from Redux state
            aggregation: aggregationEnabled ? 'enabled' : 'disabled',
            algolia: algoliaEnabled ? 'enabled' : 'disabled',
          };
          await setSystemStatus({ aggregationStatus: 'disabled', algoliaStatus: 'disabled' });
          toast.success("System triggers disabled.");
        } catch (statusError) {
          const errorMessage = statusError instanceof Error ? statusError.message : String(statusError);
          console.error("Error disabling system status before import:", statusError);
          toast.error(`Failed to disable system triggers: ${errorMessage}. Import aborted.`);
          setIsGeneratingData(false);
          return; // Abort import if disabling fails
        }

        // Import the data (passing false for runAggregation as it's handled separately)
        toast.info(`Generating ${numLocations} test locations with ${minSightings}-${maxSightings} sightings each...`);
        await importFirestoreData(jsonData, false, (message) => {
          console.log(`Test data generation: ${message}`);
        }, false); // Explicitly pass false for runAggregation
        
        toast.success(`Successfully generated and imported ${numLocations} test locations`);
        
        dispatch(fetchLocations()); // Refresh local data
        
        // Optional: Trigger recalculation and sync if checkbox is checked
        if (runRecalculationAndSync) {
          toast.info("Post-import action: Starting Recalculation & Sync...");
          await handlePerformRecalculationAndSync(); // Run the combined action
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error generating/importing test data:", error);
      toast.error(`Failed to generate/import test data: ${errorMessage}`);
    } finally {
      // *** Restore system triggers ***
      if (originalStatuses) {
        try {
          toast.info("Restoring original system trigger statuses...");
          await setSystemStatus({ 
            aggregationStatus: originalStatuses.aggregation, 
            algoliaStatus: originalStatuses.algolia 
          });
          toast.success("System triggers restored.");
        } catch (statusError) {
          const errorMessage = statusError instanceof Error ? statusError.message : String(statusError);
          console.error("Error restoring system status after import:", statusError);
          toast.error(`Failed to restore system triggers: ${errorMessage}. Please check status manually.`);
        }
      }
      // Only set loading false if not showing validation results
      if (!showValidationResults) {
        setIsGeneratingData(false);
      }
    }
  };
  
  // Handle continue after validation issues are shown
  const handleContinueAfterValidation = async (): Promise<void> => {
    setShowValidationResults(false);
    let originalStatuses: SystemStatus | null = null;
    
    // Check if there are any errors (which would prevent import)
    const hasErrors = validationIssues.some(issue => issue.severity === 'error');
    if (hasErrors) {
      toast.error("Cannot import data with validation errors. Please fix the errors and try again.");
      setIsGeneratingData(false); // Stop loading as import won't proceed
      return;
    }
    
    if (!userId) {
      toast.error("You must be logged in to generate test data with visited locations");
      setIsGeneratingData(false); // Stop loading
      return;
    }
    
    // Keep isGeneratingData true
    
    try {
      // Regenerate the data following the data model in docs/1-firestore-data-model.md
      const importData: any = {
        locations: [],
        sightings: []
      };
      
      // Create the visitedLocations subcollection path
      importData[`users/${userId}/visitedLocations`] = {};
      
      // Generate locations (Simplified for brevity - same logic as generateTestData)
      for (let i = 0; i < numLocations; i++) {
         const locationId = uuidv4();
         const locationName = getRandomLocationName();
         const coordinates = getRandomCoordinates();
         const locationType = getRandomLocationType();
        
         importData.locations.push({
           id: locationId, name: locationName, address: `${Math.floor(Math.random() * 999) + 1} Test Street`,
           url: "https://example.com", locationType,
           coordinates: { _type: 'geopoint', latitude: coordinates.latitude, longitude: coordinates.longitude },
           createdAt: { _type: 'timestamp', seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 },
           updatedAt: { _type: 'timestamp', seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 },
           averageRating: 0, ratingCount: 0, drinkCount: 0,
           lastSightingDate: { _type: 'timestamp', seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 }
         });
        
         const sightingsCount = Math.floor(Math.random() * (maxSightings - minSightings + 1)) + minSightings;
         for (let j = 0; j < sightingsCount; j++) {
           const drinksArray = drinks || [];
           if (drinksArray.length === 0) continue;
           const randomDrink = drinksArray[Math.floor(Math.random() * drinksArray.length)];
           const sightingDate = getRandomDateInPastXDays(confirmationDays);
           const sightingId = uuidv4();
           const confirmationsCount = Math.floor(Math.random() * (maxConfirmations - minConfirmations + 1)) + minConfirmations;
          
           importData[`sightings/${sightingId}/confirmations`] = [];
           const confirmationCount = confirmationsCount; // Corrected variable name
          
           importData.sightings.push({
             id: sightingId, locationId, locationName, drinkId: randomDrink.id, drinkName: randomDrink.name,
             brandId: randomDrink.brandId,
             firstSighted: { _type: 'timestamp', seconds: sightingDate.seconds, nanoseconds: sightingDate.nanoseconds },
             lastConfirmed: { _type: 'timestamp', seconds: sightingDate.seconds, nanoseconds: sightingDate.nanoseconds },
             confirmationCount
           });
          
           for (let k = 0; k < confirmationsCount; k++) {
             const confirmationDate = k === 0 ? sightingDate : getRandomDateInPastXDays(confirmationDays);
             const dummyUserId = getDummyUserId();
             const dummyUsername = getRandomUsername();
             importData[`sightings/${sightingId}/confirmations`].push({
               id: uuidv4(),
               timestamp: { _type: 'timestamp', seconds: confirmationDate.seconds, nanoseconds: confirmationDate.nanoseconds },
               userId: dummyUserId, username: dummyUsername,
               createdAt: { _type: 'timestamp', seconds: confirmationDate.seconds, nanoseconds: confirmationDate.nanoseconds }
             });
           }
         }
        
         if (Math.random() < 0.75) {
           const rating = getRandomRating();
           const visitDate = getRandomDateInPast30Days();
           importData[`users/${userId}/visitedLocations`][locationId] = {
             locationId: locationId,
             rating,
             publicNote: "",
             privateNote: "",
             visitDate: { _type: 'timestamp', seconds: visitDate.seconds, nanoseconds: visitDate.nanoseconds },
             locationName
           };
         }
      }
      
      const jsonData = JSON.stringify(importData, null, 2);

      // *** Disable system triggers before import ***
      try {
        toast.info("Disabling Aggregation and Algolia triggers for import...");
        originalStatuses = { // Store original statuses from Redux state
          aggregation: aggregationEnabled ? 'enabled' : 'disabled',
          algolia: algoliaEnabled ? 'enabled' : 'disabled',
        };
        await setSystemStatus({ aggregationStatus: 'disabled', algoliaStatus: 'disabled' });
        toast.success("System triggers disabled.");
      } catch (statusError) {
        const errorMessage = statusError instanceof Error ? statusError.message : String(statusError);
        console.error("Error disabling system status before import:", statusError);
        toast.error(`Failed to disable system triggers: ${errorMessage}. Import aborted.`);
        setIsGeneratingData(false); // Stop loading
        return; // Abort import if disabling fails
      }
      
      // Import the data (passing false for runAggregation as it's handled separately)
      toast.info(`Generating ${numLocations} test locations with ${minSightings}-${maxSightings} sightings each...`);
      await importFirestoreData(jsonData, false, (message) => {
        console.log(`Test data generation: ${message}`);
      }, false); // Explicitly pass false
      
      toast.success(`Successfully generated and imported ${numLocations} test locations`);
      
      dispatch(fetchLocations()); // Refresh local data
      
      // Optional: Trigger recalculation and sync if checkbox is checked
      if (runRecalculationAndSync) {
        toast.info("Post-import action: Starting Recalculation & Sync...");
        await handlePerformRecalculationAndSync(); // Run the combined action
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error generating/importing test data after validation:", error);
      toast.error(`Failed to generate/import test data: ${errorMessage}`);
    } finally {
      // *** Restore system triggers ***
      if (originalStatuses) {
        try {
          toast.info("Restoring original system trigger statuses...");
          await setSystemStatus({ 
            aggregationStatus: originalStatuses.aggregation, 
            algoliaStatus: originalStatuses.algolia 
          });
          toast.success("System triggers restored.");
        } catch (statusError) {
          const errorMessage = statusError instanceof Error ? statusError.message : String(statusError);
          console.error("Error restoring system status after import:", statusError);
          toast.error(`Failed to restore system triggers: ${errorMessage}. Please check status manually.`);
        }
      }
      setIsGeneratingData(false); // Final loading state update
    }
  };

  // Handle Firestore export
  const handleExportFirestore = () => {
    setIsExportingFirestore(true);
    toast.info("Starting Firestore export...");
    
    // Replace 'soda-tracker' with your actual project ID
    // TODO: Make project ID configurable or detect from environment
    fetch("http://localhost:8080/emulator/v1/projects/soda-tracker:exportDocuments", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        // TODO: Consider making output path configurable
        outputPath: "firestore_export" 
      }),
    })
    .then(response => {
      if (!response.ok) {
        // Try to parse error message from Firestore emulator response
        return response.json().then(errData => {
          throw new Error(errData.error?.message || `Export failed with status: ${response.status}`);
        }).catch(() => {
           // Fallback if error response is not JSON
           throw new Error(`Export failed with status: ${response.status}`);
        });
      }
      return response.json();
    })
    .then(data => {
      console.log("Export successful:", data);
      toast.success("Firestore data exported successfully");
    })
    .catch(error => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Export failed:", error);
      toast.error(`Firestore export failed: ${errorMessage}`);
    })
    .finally(() => {
      setIsExportingFirestore(false);
    });
  };

  return (
    <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0 overflow-y-auto" onCloseAutoFocus={(e) => e.preventDefault()}>
      <Card className="h-full w-full border-none shadow-none rounded-none overflow-auto">
        <SheetHeader className="p-4 border-b">
          <SheetTitle>Admin Panel</SheetTitle>
          <SheetDescription>
            Manage system status, data operations, and test data generation.
          </SheetDescription>
        </SheetHeader>
        <CardContent className="p-1">
          <Accordion 
            type="single" 
            defaultValue="system-status" 
            className="w-full" 
            collapsible
            value={openAccordion}
            onValueChange={handleAccordionChange}
          >
            {/* System Status Section */}
            <AccordionItem value="system-status">
              <AccordionTrigger className="text-sm px-3 py-2">
                <div className="flex justify-between items-center w-full mr-2">
                  <span>System Status</span>
                  {systemStatusLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <div className="flex gap-1">
                       <Badge variant={aggregationEnabled ? 'default' : 'destructive'} className="text-xs">
                         Agg: {aggregationEnabled ? 'On' : 'Off'}
                       </Badge>
                       <Badge variant={algoliaEnabled ? 'default' : 'destructive'} className="text-xs">
                         Sync: {algoliaEnabled ? 'On' : 'Off'}
                       </Badge>
                    </div>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 pt-1 space-y-4">
                {/* Aggregation Control */}
                <div className="space-y-2 border-b pb-3">
                   <p className="text-xs text-muted-foreground">Control Firestore aggregation triggers.</p>
                   <Button
                     variant={aggregationEnabled ? "destructive" : "default"}
                     size="sm"
                     onClick={handleToggleAggregation}
                     disabled={isLoading || isTogglingAggregation}
                     className="w-full"
                   >
                     {isTogglingAggregation ? (
                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                     ) : aggregationEnabled ? (
                       <PauseCircle className="mr-2 h-4 w-4" />
                     ) : (
                       <PlayCircle className="mr-2 h-4 w-4" />
                     )}
                     {aggregationEnabled ? 'Disable Aggregation' : 'Enable Aggregation'}
                   </Button>
                </div>

                {/* Algolia Sync Control */}
                <div className="space-y-2">
                   <p className="text-xs text-muted-foreground">Control Firestore-Algolia data mirroring.</p>
                   <AdminStatusDisplay variant="compact" />
                   <Button
                     variant={algoliaEnabled ? "destructive" : "default"}
                     size="sm"
                     onClick={handleToggleAlgolia}
                     disabled={isLoading || isTogglingAlgolia}
                     className="w-full"
                   >
                     {isTogglingAlgolia ? (
                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                     ) : algoliaEnabled ? (
                       <PauseCircle className="mr-2 h-4 w-4" />
                     ) : (
                       <PlayCircle className="mr-2 h-4 w-4" />
                     )}
                     {algoliaEnabled ? 'Disable Algolia Sync' : 'Enable Algolia Sync'}
                   </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* System Actions Section */}
            <AccordionItem value="system-actions">
              <AccordionTrigger className="text-sm px-3 py-2">
                System Actions
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 pt-1 space-y-3">
                <p className="text-xs text-muted-foreground mb-2">Perform full data recalculations and synchronization.</p>
                {/* Recalculate Aggregations Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePerformAggregation}
                  disabled={isLoading || isPerformingAggregation}
                  className="w-full"
                >
                  {isPerformingAggregation ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Recalculate Aggregations
                </Button>
                {/* Sync Algolia Index Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePerformAlgoliaSync}
                  disabled={isLoading || isPerformingAlgoliaSync}
                  className="w-full"
                >
                  {isPerformingAlgoliaSync ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Rebuild Algolia Index
                </Button>
                 {/* Recalculate & Sync Button */}
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={handlePerformRecalculationAndSync}
                   disabled={isLoading || isPerformingRecalculationAndSync}
                   className="w-full"
                 >
                   {isPerformingRecalculationAndSync ? (
                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                   ) : (
                     <RefreshCw className="mr-2 h-4 w-4" />
                   )}
                   Both
                 </Button>
                 <p className="text-xs text-muted-foreground pt-1">Note: These actions may disable system triggers. Follow toast messages for status updates.</p>
              </AccordionContent>
            </AccordionItem>

            {/* Algolia Usage Stats Section */}
            <AccordionItem value="algolia-stats">
              <AccordionTrigger className="text-sm px-3 py-2">
                <div className="flex justify-between items-center w-full mr-2">
                  <span>Algolia Usage Statistics</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 pt-1 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-muted-foreground">View current Algolia API usage.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshStats}
                    className="h-7 px-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                    <span className="sr-only">Refresh Stats</span>
                  </Button>
                </div>
                
                {/* Only render AlgoliaUsageStats when this section is open */}
                {openAccordion === "algolia-stats" && (
                  <AlgoliaUsageStats 
                    variant="compact" 
                    refreshKey={statsRefreshKey} 
                  />
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Firestore Export Section */}
            <AccordionItem value="firestore-export">
              <AccordionTrigger className="text-sm px-3 py-2">
                <div className="flex justify-between items-center w-full mr-2">
                  <span>Firestore Export</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 pt-1 space-y-4">
                <p className="text-xs text-muted-foreground">Export data from Firestore emulator.</p>
                
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportFirestore}
                    disabled={isLoading || isExportingFirestore}
                    className="w-full"
                  >
                    {isExportingFirestore ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Database className="mr-2 h-4 w-4" />
                    )}
                    Export Firestore Data
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Test Data Section */}
            <AccordionItem value="test-data">
              <AccordionTrigger className="text-sm px-3 py-2">
                Generate Test Data
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3 pt-1 space-y-4">
                <p className="text-xs text-muted-foreground">Generate or download sample data.</p>
                
                {/* Organized grid layout for controls */}
                <div className="space-y-3">
                  {/* Locations section */}
                  <div className="bg-muted/40 rounded-md p-2">
                    <h4 className="text-xs font-medium mb-2">Locations</h4>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <Label htmlFor="numLocations" className="text-xs mb-1 inline-block">Number of Locations</Label>
                        <Input
                          id="numLocations"
                          type="number"
                          min="1"
                          max="100" // Reduced max for sensibility
                          value={numLocations}
                          onChange={(e) => setNumLocations(parseInt(e.target.value) || 10)}
                          className="h-7 text-xs"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Sightings section */}
                  <div className="bg-muted/40 rounded-md p-2">
                    <h4 className="text-xs font-medium mb-2">Sightings</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="minSightings" className="text-xs mb-1 inline-block">Min Per Location</Label>
                        <Input
                          id="minSightings"
                          type="number"
                          min="1"
                          max="20"
                          value={minSightings}
                          onChange={(e) => setMinSightings(parseInt(e.target.value) || 2)}
                          className="h-7 text-xs"
                          disabled={isLoading}
                        />
                      </div>
                      <div>
                        <Label htmlFor="maxSightings" className="text-xs mb-1 inline-block">Max Per Location</Label>
                        <Input
                          id="maxSightings"
                          type="number"
                          min="1"
                          max="20"
                          value={maxSightings}
                          onChange={(e) => setMaxSightings(parseInt(e.target.value) || 5)}
                          className="h-7 text-xs"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Confirmations section */}
                  <div className="bg-muted/40 rounded-md p-2">
                    <h4 className="text-xs font-medium mb-2">Confirmations</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="minConfirmations" className="text-xs mb-1 inline-block">Min Per Sighting</Label>
                        <Input
                          id="minConfirmations"
                          type="number"
                          min="1"
                          max="10"
                          value={minConfirmations}
                          onChange={(e) => setMinConfirmations(parseInt(e.target.value) || 1)}
                          className="h-7 text-xs"
                          disabled={isLoading}
                        />
                      </div>
                      <div>
                        <Label htmlFor="maxConfirmations" className="text-xs mb-1 inline-block">Max Per Sighting</Label>
                        <Input
                          id="maxConfirmations"
                          type="number"
                          min="1"
                          max="20"
                          value={maxConfirmations}
                          onChange={(e) => setMaxConfirmations(parseInt(e.target.value) || 5)}
                          className="h-7 text-xs"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="confirmationDays" className="text-xs mb-1 inline-block">Days to Spread Confirmations</Label>
                        <Input
                          id="confirmationDays"
                          type="number"
                          min="1"
                          max="365"
                          value={confirmationDays}
                          onChange={(e) => setConfirmationDays(parseInt(e.target.value) || 30)}
                          className="h-7 text-xs"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Action section */}
                  <div className="bg-muted/40 rounded-md p-2">
                    <h4 className="text-xs font-medium mb-2">Action</h4>
                    <RadioGroup
                      defaultValue="import"
                      value={actionMode}
                      onValueChange={(value) => setActionMode(value as ActionMode)}
                      className="flex space-x-2 mb-2"
                      // Disable group when loading/generating
                      // disabled={isLoading} 
                    >
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="import" id="import" disabled={isLoading} />
                        <Label htmlFor="import" className={`text-xs ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <Upload className="h-3 w-3 inline mr-1" />
                          Import
                        </Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="download" id="download" disabled={isLoading} />
                        <Label htmlFor="download" className={`text-xs ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <Download className="h-3 w-3 inline mr-1" />
                          Download
                        </Label>
                      </div>
                    </RadioGroup>
                    
                    {/* Add recalculation/sync option checkbox */}
                    {actionMode === 'import' && (
                      <div className="flex items-center space-x-2 mb-2">
                        <Checkbox 
                          id="runRecalculationAndSync" 
                          checked={runRecalculationAndSync} 
                          onCheckedChange={(checked) => setRunRecalculationAndSync(checked === true)}
                          disabled={isLoading}
                        />
                        <Label 
                          htmlFor="runRecalculationAndSync" 
                          className={`text-xs ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        >
                          Run Recalculation & Sync after import
                        </Label>
                      </div>
                    )}
                    
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={generateTestData}
                      disabled={isTestDataGenDisabled}
                      className="w-full"
                    >
                      {isGeneratingData ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        actionMode === 'download' ? <Download className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />
                      )}
                      {actionMode === 'download' ? 'Generate & Download' : 'Generate & Import'}
                    </Button>
                  </div>
                </div>
                {/* Warning about disabling triggers */}
                {actionMode === 'import' && (
                   <p className="text-xs text-muted-foreground pt-1">Note: Importing will temporarily disable Aggregation and Algolia Sync. They will be restored afterwards.</p>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Add a check for drinks loading for the Test Data section */}
            {drinksLoading && (
              <AccordionItem value="test-data-loading" disabled>
                <AccordionTrigger className="text-sm px-3 py-2 text-muted-foreground">
                  Loading Drinks for Test Data...
                </AccordionTrigger>
              </AccordionItem>
            )}

          </Accordion>
        </CardContent>
      </Card>
      
      {/* Validation Results Dialog */}
      <AlertDialog open={showValidationResults} onOpenChange={setShowValidationResults}>
        <AlertDialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Test Data Validation Results
              {validationIssues.length > 0 && (
                <div className="flex gap-2 mt-2">
                  <Badge variant="destructive">
                    {validationIssues.filter(i => i.severity === 'error').length} Errors
                  </Badge>
                  <Badge variant="secondary">
                    {validationIssues.filter(i => i.severity === 'warning').length} Warnings
                  </Badge>
                </div>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {validationIssues.some(i => i.severity === 'error')
                ? "The generated test data contains errors that prevent import."
                : validationIssues.length > 0
                  ? "The generated test data contains warnings. Review before continuing."
                  : "The generated test data is valid."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="overflow-auto flex-1 my-4 border rounded-md">
            {validationIssues.length === 0 ? (
              <div className="p-4 text-center text-green-500">
                No validation issues found!
              </div>
            ) : (
              <div className="divide-y">
                {validationIssues.map((issue, index) => (
                  <div 
                    key={index} 
                    className={`p-3 flex gap-2 ${
                      issue.severity === 'error' ? 'bg-red-50 dark:bg-red-950/20' : 'bg-yellow-50 dark:bg-yellow-950/20'
                    }`}
                  >
                    <div className={`text-sm font-semibold shrink-0 ${ // Added shrink-0
                      issue.severity === 'error' ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {issue.severity === 'error' ? 'Error:' : 'Warning:'}
                    </div>
                    <div className="text-sm break-words">{issue.message}</div> {/* Added break-words */}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsGeneratingData(false)}>Cancel</AlertDialogCancel> {/* Ensure loading stops on cancel */}
            {!validationIssues.some(i => i.severity === 'error') && (
              <AlertDialogAction onClick={handleContinueAfterValidation}>
                Continue with Import
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SheetContent>
  );
});

// Set displayName for React DevTools
AdminPanelContent.displayName = 'AdminPanelContent';

export default AlgoliaAdminBox;